"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { useForgotPasswordMutation } from "@saas/account/data-access/@mutation/forgot-password/use-forgot-password.mutation";
import { usePostMetadataRecoveryMutation } from "@saas/account/data/@mutation/post-metadata-recovery/use-post-metadata-recovery.mutation";
import { env } from "@saas/config/shared/env/env";
import { useLoyaltyBrandContext } from "@saas/loyalty/feature/@context/loyalty-brand/loyalty-brand.context";
import { createLoyaltyUrl } from "@saas/loyalty/utils/create-loyalty-url/create-loyalty-url";
import { BaseButton } from "@saas/shared/ui/base-button/base-button";
import { BaseInput } from "@saas/shared/ui/base-input/base-input";

import { useForm } from "react-hook-form";
import isEmail from "validator/lib/isEmail";

type ForgotPasswordFormData = { email: string };

export const ForgotPasswordClientPage = () => {
  const router = useRouter();
  const [passwordRecoverySent, setPasswordRecoverySent] = useState(false);

  const { brandSlug } = useLoyaltyBrandContext();
  const {
    mutateAsync: mutateMetadataRecovery,
    isLoading: isMetadataRecoveryLoading,
  } = usePostMetadataRecoveryMutation();

  const {
    formState: { errors, isValid },
    register,
    handleSubmit,
    watch,
  } = useForm<ForgotPasswordFormData>({
    mode: "onChange",
  });

  const {
    mutateAsync: recoverAccount,
    recoveryData,
    isLoading: isForgotPasswordLoading,
  } = useForgotPasswordMutation();

  const emailValue = watch("email");

  const handleRecovery = async (data: ForgotPasswordFormData) => {
    try {
      await recoverAccount(
        { email: data.email },
        {
          onSuccess: () => {
            setPasswordRecoverySent(true);
          },
        }
      );

      await mutateMetadataRecovery({
        email: data.email,
        recoveryFrom: createLoyaltyUrl({
          brandSlug,
          pathname: "/",
        }),
      });

      setPasswordRecoverySent(true);
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const handleRedirectToLogin = () => {
    const loginUrl = createLoyaltyUrl({
      brandSlug,
      pathname: "/login",
    });

    router.push(loginUrl);
  };

  return (
    <div
      className={
        "z-1 mx-auto -mt-[90px] flex min-h-screen w-full justify-center px-5 md:px-6 2xl:items-center"
      }
    >
      {!passwordRecoverySent ? (
        <form
          onSubmit={handleSubmit(handleRecovery)}
          className={
            "bg-neutral-N0 mt-[100px] flex h-max max-w-lg flex-col gap-6 rounded-2xl p-6 md:mt-[100px] md:p-24"
          }
        >
          <p
            className={"text-[22px] font-bold md:text-[32px]"}
            data-testid={"forgot-password__text__title"}
          >
            Lupa Kata Sandi
          </p>

          <p
            className={"text-base"}
            data-testid={"forgot-password__text__subtitle"}
          >
            Masukkan email kamu yang terdaftar di {env.APP_NAME}. Instruksi
            perubahan email akan dikirimkan ke email tersebut.
          </p>

          <BaseInput
            testid={"forgot-password__text-field__email"}
            placeholder={"Email"}
            label={"Email"}
            type={"email"}
            error={!!errors.email?.message?.length}
            hint={errors.email?.message || ""}
            {...register("email", {
              required: "Wajib diisi.",
              validate: (value) => {
                if (!isEmail(value))
                  return "Harap isi dengan format yang benar.";

                return true;
              },
            })}
          />

          <BaseButton
            testid={"forgot-password__button__send-instruction"}
            disabled={!isValid || !recoveryData?.id}
            isLoading={isForgotPasswordLoading || isMetadataRecoveryLoading}
            variant={"primary"}
            size={"big"}
            type={"submit"}
          >
            Kirim Instruksi
          </BaseButton>
        </form>
      ) : (
        <div
          className={
            "bg-neutral-N0 mt-[100px] flex h-max max-w-lg flex-col gap-6 rounded-2xl p-6 md:mt-[100px] md:p-24"
          }
        >
          <p
            className={"text-[22px] font-bold md:text-[32px]"}
            data-testid={"forgot-password-popup__text__title"}
          >
            Instruksi Terkirim
          </p>

          <p
            className={"text-base"}
            data-testid={"forgot-password-popup__text__subtitle"}
          >
            {`Kami telah mengirimkan instruksi untuk mengganti kata sandi ke email ${emailValue}, cek kotak masuk dan juga spam-mu.`}
          </p>
          <BaseButton
            testid={"forgot-password-popup__button__ok"}
            disabled={!recoveryData?.id}
            variant={"primary"}
            size={"big"}
            onClick={handleRedirectToLogin}
          >
            OK
          </BaseButton>
        </div>
      )}
    </div>
  );
};
